import { useEffect, useMemo, useRef, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Container from '@mui/material/Container';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import LoadingOverlay from 'react-loading-overlay-ts';
import PageHeader from '../components/PageHeader';
import Config from '../models/Config';
import {
  HorseOption,
  RiderOption,
  RiderResultGroup,
  RiderResultsRetainedFilter,
  SourceShowResult,
} from '../models/RiderResults';
import {
  getHorseOptionsForRiders,
  getRiderOptions,
  groupRiderResults,
} from '../services/RiderResultsService';
import {
  clearRiderResultsRetainedFilter,
  loadRiderResultsRetainedFilter,
  saveRiderResultsRetainedFilter,
} from '../services/RiderResultsFavoriteService';
import { GoogleSheetsService, Show } from '../services/GoogleSheetsService';

const formatShowDate = (showDateTime: string) => {
  if (!showDateTime) return '';
  const date = new Date(showDateTime);
  return Number.isNaN(date.getTime()) ? showDateTime : date.toLocaleDateString();
};

const retainedFilterStillValid = (
  filter: RiderResultsRetainedFilter,
  riders: RiderOption[],
  horses: HorseOption[],
) =>
  filter.riderNames.every((riderName) => riders.some((rider) => rider.name === riderName)) &&
  filter.horseNames.every((horseName) => horses.some((horse) => horse.name === horseName));

const retainedFilterStaleMessage = 'Retained filter no longer matches current rider results. Revise or clear it.';

function RiderResultPage() {
  const [allResults, setAllResults] = useState<SourceShowResult[]>([]);
  const [shows, setShows] = useState<Show[]>([]);
  const [selectedRiders, setSelectedRiders] = useState<RiderOption[]>([]);
  const [selectedHorses, setSelectedHorses] = useState<HorseOption[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [filterMessage, setFilterMessage] = useState<string | null>(null);
  const [storageUnavailable, setStorageUnavailable] = useState<boolean>(false);
  const [restoreAttempted, setRestoreAttempted] = useState<boolean>(false);
  const hasLoadedResults = useRef(false);

  useEffect(() => {
    document.title = `${Config.TAB_PREFIX}Rider Results`;
  }, []);

  useEffect(() => {
    let mounted = true;

    const fetchRiderResults = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const service = new GoogleSheetsService();
        const [results, showList] = await Promise.all([service.getAllShowResults(), service.getAllShows()]);
        if (!mounted) return;
        setAllResults(results);
        setShows(showList);
        hasLoadedResults.current = true;
      } catch {
        if (mounted) {
          setError('Failed to load rider results. Please try again.');
        }
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    };

    fetchRiderResults();

    return () => {
      mounted = false;
    };
  }, []);

  const riderOptions = useMemo(() => getRiderOptions(allResults), [allResults]);

  const horseOptions = useMemo(
    () =>
      getHorseOptionsForRiders(
        allResults,
        selectedRiders.map((rider) => rider.name),
      ),
    [allResults, selectedRiders],
  );

  const hasActiveSelection = selectedRiders.length + selectedHorses.length > 0;

  const resultGroups: RiderResultGroup[] = useMemo(
    () =>
      selectedRiders.length > 0 || selectedHorses.length > 0
        ? groupRiderResults(
            allResults,
            shows,
            selectedRiders.map((rider) => rider.name),
            selectedHorses.map((horse) => horse.name),
          )
        : [],
    [allResults, selectedHorses, selectedRiders, shows],
  );

  useEffect(() => {
    if (restoreAttempted || !hasLoadedResults.current) return;
    const retainedFilter = loadRiderResultsRetainedFilter();
    if (!retainedFilter) {
      setRestoreAttempted(true);
      return;
    }

    const riders = retainedFilter.riderNames
      .map((riderName) => riderOptions.find((option) => option.name === riderName))
      .filter((rider): rider is RiderOption => Boolean(rider));

    const horses = getHorseOptionsForRiders(
      allResults,
      riders.map((rider) => rider.name),
    ).filter((horse) => retainedFilter.horseNames.includes(horse.name));

    if (
      riders.length !== retainedFilter.riderNames.length ||
      horses.length !== retainedFilter.horseNames.length ||
      !retainedFilterStillValid(retainedFilter, riderOptions, horses)
    ) {
      setFilterMessage(retainedFilterStaleMessage);
      setRestoreAttempted(true);
      return;
    }

    setSelectedRiders(riders);
    setSelectedHorses(horses);
    setFilterMessage('Last filter restored.');
    setStorageUnavailable(false);
    setRestoreAttempted(true);
  }, [allResults, restoreAttempted, riderOptions]);

  useEffect(() => {
    if (!restoreAttempted) return;
    if (hasActiveSelection) {
      const saved = saveRiderResultsRetainedFilter(
        selectedRiders.map((rider) => rider.name),
        selectedHorses.map((horse) => horse.name),
      );
      if (!saved) {
        setStorageUnavailable(true);
        setFilterMessage('Last filter could not be retained on this device.');
        return;
      }
      setStorageUnavailable(false);
      if (filterMessage === retainedFilterStaleMessage) {
        setFilterMessage(null);
      }
      return;
    }

    const cleared = clearRiderResultsRetainedFilter();
    if (!cleared) {
      setStorageUnavailable(true);
      setFilterMessage('Last filter could not be cleared on this device.');
      return;
    }
    setStorageUnavailable(false);
  }, [filterMessage, hasActiveSelection, restoreAttempted, selectedHorses, selectedRiders]);

  const handleRiderChange = (riders: RiderOption[]) => {
    setSelectedRiders(riders);
    setSelectedHorses([]);
    setFilterMessage(null);
  };

  return (
    <LoadingOverlay active={isLoading} className="loading-overlay">
      <Container maxWidth="md" sx={{ marginBottom: '60px' }} className="page-header">
        <PageHeader header="Rider Results" />
        {error && (
          <Typography color="error" role="alert">
            {error}
          </Typography>
        )}

        {filterMessage && (
          <Typography color={storageUnavailable ? 'error' : 'text.secondary'} role="status" sx={{ mb: 2 }}>
            {filterMessage}
          </Typography>
        )}

        <Stack spacing={2}>
          <Autocomplete
            multiple
            options={riderOptions}
            getOptionLabel={(option) => option.name}
            value={selectedRiders}
            onChange={(_, riders) => handleRiderChange(riders)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} key={option.name} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Riders" />}
          />

          <Autocomplete
            multiple
            options={horseOptions}
            getOptionLabel={(option) => option.name}
            value={selectedHorses}
            onChange={(_, horses) => {
              setSelectedHorses(horses);
              setFilterMessage(null);
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip label={option.name} {...getTagProps({ index })} key={option.name} />
              ))
            }
            renderInput={(params) => <TextField {...params} label="Horses" />}
          />

          {!hasActiveSelection && !isLoading && !error && (
            <Typography variant="body2">Select one or more riders or horses to view results across all shows.</Typography>
          )}

          {hasActiveSelection && resultGroups.length === 0 && !isLoading && (
            <Typography>No results found for this rider or horse selection.</Typography>
          )}

          {resultGroups.map((group) => (
            <Box key={group.showId} sx={{ mt: 2 }}>
              <div className="show-results-list-row card-row--primary">
                <div className="show-results-list-title">{group.showName}</div>
                <div className="show-results-list-date">{formatShowDate(group.showDateTime)}</div>
              </div>
              <Stack spacing={0.5} sx={{ mt: 1 }}>
                {group.results.map((result) => (
                  <Box
                    key={`${result.rowIndexId}-${result.showId}`}
                    className="show-result-row"
                    sx={{ display: 'grid', gridTemplateColumns: '2fr 2fr 2fr 1fr', gap: 1 }}
                  >
                    <Typography>{result.division || 'Class unavailable'}</Typography>
                    <Typography>{result.riderName || 'Rider unavailable'}</Typography>
                    <Typography>{result.horseName || 'Horse unavailable'}</Typography>
                    <Typography>{result.time || 'Time unavailable'}</Typography>
                  </Box>
                ))}
              </Stack>
            </Box>
          ))}
        </Stack>
      </Container>
    </LoadingOverlay>
  );
}

export default RiderResultPage;
